import {
  inject,
  signal,
  WritableSignal,
  Signal,
  Injectable,
  OnDestroy,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';

import { PaginatedApiResponse } from '@shared/models/securepay';
import { ChargeBack } from '../../services/charge-back';

import {
  catchError,
  debounceTime,
  finalize,
  map,
  of,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import {
  Dialog,
  Notification,
  ViewChargebackDialog,
  chargebackStatusDisplay,
  formatSecurepayDateTimeDisplay,
  isMerchantChargebackActionable,
  parseChargebackOrigin,
  parseChargebackStatus,
  parseChargebackStatusFromRecord,
} from '@shared';
import { MatDialog } from '@angular/material/dialog';
import { DeclineChargebackDialog } from '../../../pages/chargeback/decline-chargeback-dialog/decline-chargeback-dialog';
import { PartialRefundDialog } from '../../../pages/chargeback/partial-refund-dialog/partial-refund-dialog';

export interface ChargebackQueryParamsState {
  page: number;
  pageSize: number;
  search: string | undefined;
  startDate: string | Date | undefined;
  endDate: string | Date | undefined;
}

export type ChargebackDetailUiStatus =
  | 'idle'
  | 'loading'
  | 'success'
  | 'error'
  | 'empty';

export interface ChargebackDetailUiState {
  status: ChargebackDetailUiStatus;
  /** Present for `error` / `empty` (optional helper text). */
  message?: string | null;
}

const DETAIL_UI_INITIAL: ChargebackDetailUiState = { status: 'idle' };

@Injectable({ providedIn: 'root' })
export class ChargebackStore implements OnDestroy {
  public statusArray: { label: string; value: boolean | string | null }[] = [
    { label: 'All', value: null },
    { label: 'Successful', value: true },
    { label: 'Failed', value: false },
  ];
  public statusFilter: string | null = null;

  public tableHeaders: { label: string; key: string }[] = [
    {
      label: 'Case ref',
      key: 'caseRef',
    },
    {
      label: 'Transaction ID',
      key: 'transactionRef',
    },
    {
      label: 'Amount',
      key: 'amount',
    },
    {
      label: 'Customer Email',
      key: 'customerEmail',
    },
    {
      label: 'Date Raised ',
      key: 'dateRaised',
    },
    {
      label: 'Status ',
      key: 'chargebackStatusLabel',
    },
    {
      label: 'Origin ',
      key: 'originParsed',
    },
    // {
    //   label: 'Channel',
    //   key: 'virtualAccountNumber',
    // },
    {
      label: 'Action Deadline',
      key: 'actionDeadline',
    },
  ];

  private chargebackService = inject(ChargeBack);
  private datePipe = inject(DatePipe);
  private currencyPipe = inject(CurrencyPipe);
  private dialog = inject(MatDialog);
  private notification = inject(Notification);
  private spayDialog = inject(Dialog);

  // --- Store STATE SIGNALS (Read by Component) ---
  // amazonq-ignore-next-line
  public chargeback: WritableSignal<any[]> = signal([]);

  public isLoading: WritableSignal<boolean> = signal(false);
  public isLoadingMetrics: WritableSignal<boolean> = signal(false);
  public isDownloading: WritableSignal<boolean> = signal(false);
  public error: WritableSignal<string | null> = signal(null);
  public totalCount: WritableSignal<number> = signal(0);

  /** UI for GET single chargeback (row click → detail fetch). */
  private readonly chargebackDetailUiSignal: WritableSignal<ChargebackDetailUiState> =
    signal(DETAIL_UI_INITIAL);
  readonly chargebackDetailUi = this.chargebackDetailUiSignal.asReadonly();

  // --- QUERY STATE (Drives the API Request) ---
  private queryParamsState: WritableSignal<ChargebackQueryParamsState> = signal(
    {
      page: 0,
      pageSize: 10,
      search: undefined,
      startDate: undefined,
      endDate: undefined,
    },
  );

  public queryParams: Signal<ChargebackQueryParamsState> =
    this.queryParamsState.asReadonly();

  // --- RxJS DEBOUNCE SETUP ---
  private filterChange$ = new Subject<void>();
  private storeSubscription = new Subscription();
  // amazonq-ignore-next-line
  selectedChargeback: any;

  constructor() {
    this.storeSubscription.add(
      this.filterChange$
        .pipe(
          debounceTime(300), // Wait 300ms before triggering the load
        )
        .subscribe(() => this.loadChargeback()),
    );
  }

  // --- ACTION: CORE NETWORK CALL ---
  public loadChargeback(): void {
    this.isLoading.set(true);
    this.error.set(null);

    const params = this.queryParamsState();

    this.storeSubscription.add(
      this.chargebackService
        .getAllChargebacks(params)
        .pipe(
          finalize(() => this.isLoading.set(false)),
          catchError((err) => {
            this.error.set(err.message || 'Network request failed.');
            // Return an empty, successful-looking response to complete the stream
            const emptyData: PaginatedApiResponse<any[]> = {
              recordsTotal: 0,
              recordsFiltered: 0,
              data: [],
              pageSize: params.pageSize || 0,
              hasNextPage: false,
              hasPreviousPage: false,
              totalPages: 0,
              pageIndex: params.page || 0,
            };
            return of({
              success: false,
              statusCode: 'ERROR',
              message: 'API Fail',
              data: emptyData,
            });
          }),
        )
        .subscribe((apiResponse) => {
          if (apiResponse.success && apiResponse.data) {
            this.chargeback.set(
              apiResponse.data.data.map((chargeback) => {
                const amountSource =
                  chargeback.amount ??
                  chargeback.disputedAmount ??
                  chargeback.refundAmount;
                const originParsed = parseChargebackOrigin(chargeback.origin);
                const statusParsed = parseChargebackStatus(
                  chargeback.status ??
                    chargeback.chargebackStatus ??
                    chargeback.caseStatus,
                  { caseInsensitive: true },
                );
                return {
                  ...chargeback,
                  dateRaised: formatSecurepayDateTimeDisplay(
                    chargeback.dateRaised,
                  ),
                  actionDeadline: formatSecurepayDateTimeDisplay(
                    chargeback.actionDeadline,
                  ),
                  amount:
                    this.currencyPipe.transform(
                      amountSource ?? 0,
                      'NGN',
                      '₦',
                    ) || '₦0.00',
                  chargebackStatusLabel:
                    statusParsed != null
                      ? chargebackStatusDisplay(statusParsed)
                      : chargeback.status != null ||
                          chargeback.chargebackStatus != null
                        ? String(
                            chargeback.status ?? chargeback.chargebackStatus,
                          )
                        : '—',
                  originParsed: originParsed ?? '—',
                };
              }),
            );
            // Use the filtered count for accurate pagination display
            this.totalCount.set(apiResponse.data.recordsTotal);
            this.queryParamsState.update((p) => ({
              ...p,
              page: apiResponse.data.pageIndex,
              pageSize: apiResponse.data.pageSize,
            }));
          } else {
            this.chargeback.set([]);
            this.totalCount.set(0);
            this.error.set(
              apiResponse.message || 'API returned failure status.',
            );
          }
        }),
    );
  }

  onAcceptChargeback() {
    if (this.selectedChargeback.refundType === 1) {
      this.dialog
        .open(PartialRefundDialog, {
          width: '400px',
          maxWidth: '500px',
          data: {
            maxRefundAmount: this.selectedChargeback.amount,
            originalAmountDisplay: this.selectedChargeback.amount,
          },
        })
        .afterClosed()
        .pipe(
          tap((payload) => {
            this.handleAccept({
              amount: payload.refundAmount,
              partialRefundReason: payload.reason,
            });
          }),
        )
        .subscribe();
    } else {
      this.handleAccept();
    }
  }

  handleAccept(
    payload: { amount: string; partialRefundReason: string } = {
      amount: '0',
      partialRefundReason: '',
    },
  ) {
    this.chargebackService
      .acceptChargeBack({
        chargebackId: this.selectedChargeback.chargebackId,
        ...payload,
      })
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.notification.success('success', response.message);
          } else {
            this.notification.info('success', response.message);
          }
          this.loadChargeback();
        },
        error: (err) => {
          this.notification.error(
            err.message || 'Failed to raise chargeback',
            'error',
          );
        },
      });
  }

  onDeclineChargeback() {
    this.dialog
      .open(DeclineChargebackDialog, {
        width: '400px',
        maxWidth: '500px',
        maxHeight: '90vh',
      })
      .afterClosed()
      .subscribe((chargebackForm) => {
        if (!chargebackForm) {
          return;
        }
        this.chargebackService
          .declineChargeBack({
            chargebackId: this.selectedChargeback.chargebackId,
            merchantComment: chargebackForm.merchantComment,
            proofFileUrl: chargebackForm.proofFileUrl,
          })
          .subscribe({
            next: (response: any) => {
              if (response.success) {
                this.notification.success('success', response.message);
              } else {
                this.notification.info('success', response.message);
              }
              this.loadChargeback();
            },
            error: (err) => {
              this.notification.error(
                err.message || 'Failed to raise chargeback',
                'error',
              );
            },
          });
      });
  }

  // --- ACTION: FILTER/SEARCH UPDATE ---
  public updateQuery(updates: Partial<ChargebackQueryParamsState>): void {
    this.queryParamsState.update((current) => {
      const newParams = { ...current, ...updates };

      // Always reset page index to 0 when filters (like businessName) change
      const hasFilterChanges = Object.keys(updates).some(
        (key) => key !== 'page' && key !== 'pageSize',
      );
      return newParams;
    });

    // Notify the debouncer that a change has occurred
    this.filterChange$.next();
  }

  setSelectedChargeback(chargeback: any) {
    this.selectedChargeback = chargeback;
  }

  /** Accept/Decline in the row menu only for statuses that still allow merchant response. */
  isSelectedChargebackMerchantActionable(): boolean {
    const row = this.selectedChargeback as Record<string, unknown> | null;
    if (row == null) {
      return false;
    }
    const status = parseChargebackStatusFromRecord(row);
    if (status == null) {
      return true;
    }
    return isMerchantChargebackActionable(status);
  }

  /** Clears inline detail fetch UI (success / error / empty banners). */
  clearChargebackDetailUi(): void {
    this.chargebackDetailUiSignal.set(DETAIL_UI_INITIAL);
  }

  onViewChargeBack(chargeback: any) {
    const chargebackId =
      chargeback?.chargebackId ?? chargeback?.id ?? chargeback?.chargeBackId;
    if (chargebackId == null || String(chargebackId).trim() === '') {
      this.chargebackDetailUiSignal.set({
        status: 'error',
        message: 'Unable to open chargeback: missing identifier.',
      });
      return;
    }

    this.chargebackDetailUiSignal.set({
      status: 'loading',
      message: null,
    });

    const sub = this.chargebackService
      .getChargebackById(String(chargebackId))
      .pipe(
        finalize(() => {
          const s = this.chargebackDetailUiSignal();
          if (s.status === 'loading') {
            this.chargebackDetailUiSignal.set(DETAIL_UI_INITIAL);
          }
        }),
      )
      .subscribe({
        next: (response) => {
          if (!response.success || response.data == null) {
            this.chargebackDetailUiSignal.set({
              status: 'error',
              message:
                response.message?.trim() ||
                'Could not load chargeback details.',
            });
            return;
          }

          const detail = response.data as Record<string, unknown>;
          if (this.isChargebackDetailPayloadEmpty(detail)) {
            this.chargebackDetailUiSignal.set({
              status: 'empty',
              message: 'No chargeback details were returned for this case.',
            });
            return;
          }

          const merged = { ...chargeback, ...detail };
          this.selectedChargeback = merged;
          this.chargebackDetailUiSignal.set({
            status: 'success',
            message: 'Chargeback details loaded.',
          });
          this.openViewChargebackDialog(merged);
          window.setTimeout(() => {
            if (this.chargebackDetailUiSignal().status === 'success') {
              this.chargebackDetailUiSignal.set(DETAIL_UI_INITIAL);
            }
          }, 4000);
        },
        error: (err: { message?: string }) => {
          this.chargebackDetailUiSignal.set({
            status: 'error',
            message:
              err?.message?.trim() ||
              'Failed to load chargeback details. Please try again.',
          });
        },
      });

    this.storeSubscription.add(sub);
  }

  private isChargebackDetailPayloadEmpty(
    detail: Record<string, unknown>,
  ): boolean {
    const keys = Object.keys(detail);
    if (keys.length === 0) {
      return true;
    }
    return keys.every((k) => {
      const v = detail[k];
      if (v === null || v === undefined) {
        return true;
      }
      if (typeof v === 'string' && v.trim() === '') {
        return true;
      }
      if (Array.isArray(v) && v.length === 0) {
        return true;
      }
      if (
        typeof v === 'object' &&
        v !== null &&
        !Array.isArray(v) &&
        Object.keys(v as Record<string, unknown>).length === 0
      ) {
        return true;
      }
      return false;
    });
  }

  private openViewChargebackDialog(chargeback: Record<string, unknown>) {
    const sub = this.dialog
      .open(ViewChargebackDialog, {
        width: 'min(500px, 100vw)',
        maxWidth: '100vw',
        height: '100%',
        maxHeight: '100vh',
        position: { right: '0', top: '0' },
        panelClass: 'chargeback-view-side-dialog',
        hasBackdrop: true,
        data: {
          chargeback,
          viewerRole: 'merchant',
        },
      })
      .afterClosed()
      .subscribe({
        next: (status: 'accept' | 'decline') => {
          if (status === 'accept') {
            this.onAcceptChargeback();
          } else if (status === 'decline') {
            this.onDeclineChargeback();
          }
        },
      });

    this.storeSubscription.add(sub);
  }

  // --- ACTION: PAGINATION UPDATE ---
  public changePage(page: number): void {
    this.queryParamsState.update((p) => ({ ...p, page }));
    // Page changes require an immediate load, bypassing the debounce
    this.loadChargeback();
  }

  ngOnDestroy(): void {
    this.storeSubscription.unsubscribe();
  }
}
